import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, Loader2, Check } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DiscountPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if user has already seen the popup
    const hasSeenPopup = localStorage.getItem('goat_popup_seen');
    
    if (!hasSeenPopup) {
      // Show popup after 5 seconds
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('goat_popup_seen', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/email-signup`, { email });
      setDiscountCode(response.data.discountCode);
      setSubmitted(true);
      localStorage.setItem('goat_popup_seen', 'true');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(discountCode);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 overflow-hidden"
            onClick={handleClose}
          />
          
          {/* Popup Container - Centers the popup */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none overflow-hidden">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="w-full max-w-md pointer-events-auto"
              data-testid="discount-popup"
            >
              <div className="bg-[#09090b] text-white p-6 sm:p-8 relative overflow-hidden mx-auto">
                {/* Close button */}
                <button
                  onClick={handleClose}
                  className="absolute top-3 right-3 sm:top-4 sm:right-4 text-zinc-400 hover:text-white transition-colors z-10 p-1"
                  data-testid="popup-close"
                >
                  <X className="w-5 h-5 sm:w-5 sm:h-5" />
                </button>

                {/* Decorative element */}
                <div className="absolute -right-8 -top-8 w-32 h-32 bg-[#00ff9d] rounded-full opacity-20 blur-2xl" />
                
                {!submitted ? (
                  <>
                    {/* Icon */}
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#00ff9d] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                      <Gift className="w-6 h-6 sm:w-7 sm:h-7 text-black" />
                    </div>

                    {/* Content */}
                    <h2 className="font-heading font-black text-xl sm:text-2xl md:text-3xl uppercase tracking-tight">
                      Get 10% Off
                    </h2>
                    <p className="text-zinc-400 text-sm sm:text-base mt-2 mb-4 sm:mb-6">
                      Join the G.O.A.T. community and get 10% off your first order. No spam, just gear drops and exclusive offers.
                    </p>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                      <div>
                        <Input
                          type="email"
                          placeholder="Enter your email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="h-11 sm:h-12 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-[#00ff9d] text-base"
                          data-testid="popup-email-input"
                        />
                        {error && <p className="text-[#ff4d6d] text-sm mt-1">{error}</p>}
                      </div>
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full h-11 sm:h-12 bg-[#00ff9d] text-black hover:bg-[#00dd88] font-bold uppercase tracking-wider rounded-full text-sm sm:text-base"
                        data-testid="popup-submit"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          'Unlock My Discount'
                        )}
                      </Button>
                    </form>

                    <p className="text-zinc-500 text-xs text-center mt-3 sm:mt-4">
                      By signing up, you agree to receive marketing emails.
                    </p>
                  </>
                ) : (
                  /* Success State */
                  <div className="text-center py-2 sm:py-4">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#00ff9d] rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                      <Check className="w-7 h-7 sm:w-8 sm:h-8 text-black" />
                    </div>
                    
                    <h2 className="font-heading font-black text-xl sm:text-2xl uppercase tracking-tight">
                      You're In!
                    </h2>
                    <p className="text-zinc-400 text-sm sm:text-base mt-2 mb-4 sm:mb-6">
                      Use this code at checkout for 10% off:
                    </p>

                    {/* Discount Code */}
                    <div 
                      className="bg-zinc-900 border-2 border-dashed border-[#00ff9d] p-3 sm:p-4 mb-4 sm:mb-6 cursor-pointer hover:bg-zinc-800 transition-colors"
                      onClick={copyCode}
                      data-testid="discount-code"
                    >
                      <p className="font-heading font-black text-2xl sm:text-3xl text-[#00ff9d] tracking-wider">
                        {discountCode}
                      </p>
                      <p className="text-zinc-500 text-xs mt-1">Click to copy</p>
                    </div>

                    <Button
                      onClick={handleClose}
                      className="bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wider rounded-full px-6 sm:px-8 text-sm sm:text-base"
                      data-testid="popup-start-shopping"
                    >
                      Start Shopping
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
